import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { OpenInNew, CheckCircleOutline, HighlightOff, Edit } from '@material-ui/icons'

import { DrawerBodyWrapper, TOAST_TYPE } from '@base'
import paths from 'src/routes/paths'
import { strings, MODAL_TYPES, APPROVAL_STATUS, APPROVAL_ACTION } from '@constants'
import { displayAmount } from '@utils'
import { getUserCreditSummary, creditApprovalAction } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { toggleModal } from '@data/state/action/root'
import Table from '@baseV2/composite/table'

import { Pill, Title, LinkText, KeyValue, KeyLabel, KeyVal, ActionsCell, IconButton, DrawerSummaryCard, SummaryRow } from '../styles'
import { getStatusPill, isApprovalMember } from '../ListApprovalMetrics/utils'

const UnapprovedEnquiries = ({ row }) => {
	const dispatch = useDispatch()
	const user = useSelector((state) => state.getIn(['user', 'user']))
	const userId = user?.user_id
	const [summary, setSummary] = useState(null)
	const [editing, setEditing] = useState({})

	const organisationId = row?.user_details?.organisation_id

	const loadSummary = useCallback(() => {
		dispatch(showLoader(true))
		getUserCreditSummary(organisationId)
			.then((response) => setSummary(response ?? null))
			.catch(() => setSummary(null))
			.finally(() => dispatch(showLoader(false)))
	}, [dispatch, organisationId])

	useEffect(() => {
		loadSummary()
	}, [loadSummary])

	const enquiries = summary?.enquiries || []

	const startEdit = (item) => setEditing((prev) => ({ ...prev, [item?.reference_id]: true }))

	const runAction = (item, action) => {
		const isApprove = action === APPROVAL_ACTION.APPROVE
		dispatch(
			toggleModal(true, MODAL_TYPES.CONFIRMATION, {
				overlayClickable: true,
				title: strings(isApprove ? 'approve_confirmation' : 'reject_confirmation'),
				confirmAction: (actionType) => {
					if (actionType) {
						dispatch(showLoader(true))
						creditApprovalAction({ action, creditApprovalId: item?.credit_approval_id, memberUserId: userId, remarks: '' })
							.then(() => {
								dispatch(
									showToast(true, strings(isApprove ? 'approved_successfully' : 'rejected_successfully'), {
										type: TOAST_TYPE.SUCCESS,
									})
								)
								setEditing({})
								loadSummary()
							})
							.catch((err) => {
								dispatch(showToast(true, err?.response?.data?.message || strings('something_went_wrong'), { type: TOAST_TYPE.ERROR }))
							})
							.finally(() => {
								dispatch(showLoader(false))
							})
					}
					dispatch(toggleModal(false))
				},
			})
		)
	}

	const handleApprove = (item) => runAction(item, APPROVAL_ACTION.APPROVE)
	const handleReject = (item) => runAction(item, APPROVAL_ACTION.REJECT)

	const columns = useMemo(
		() => [
			{
				label: strings('enquiry_id'),
				key: (item) => item,
				sortable: false,
				renderer: (item) => (
					<LinkText href={paths.admin_manage_enquiry_manage(item?.reference_id)} target='_blank' rel='noopener noreferrer'>
						{item?.reference_id || '--'}
						<OpenInNew />
					</LinkText>
				),
			},
			{
				label: `${strings('required_limit')} / ${strings('po_amount')}`,
				key: (item) => item,
				sortable: false,
				renderer: (item) => (
					<div style={{ textAlign: 'center' }}>
						<div>{displayAmount(item?.required_credit_limit || 0)}</div>
						<small>
							<KeyVal>{displayAmount(item?.po_amount || 0)}</KeyVal>
						</small>
					</div>
				),
			},
			{
				label: strings('status'),
				key: (item) => item,
				sortable: false,
				renderer: (item) => {
					const pill = getStatusPill(item?.status)
					return (
						<Pill color={pill.color} variant='subtle'>
							{pill.label}
						</Pill>
					)
				},
			},

			{
				label: strings('actions'),
				key: (item) => item,
				align: 'right',
				sortable: false,
				renderer: (item) => {
					if (!isApprovalMember(item, userId)) return null
					const showActions = item?.status === APPROVAL_STATUS.PENDING || editing[item?.reference_id]
					return showActions ? (
						<ActionsCell>
							<IconButton $color='success' onClick={() => handleApprove(item)}>
								<CheckCircleOutline />
							</IconButton>
							<IconButton $color='error' onClick={() => handleReject(item)}>
								<HighlightOff />
							</IconButton>
						</ActionsCell>
					) : (
						<ActionsCell>
							<IconButton onClick={() => startEdit(item)}>
								<Edit />
							</IconButton>
						</ActionsCell>
					)
				},
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[userId, editing]
	)

	return (
		<DrawerBodyWrapper>
			<DrawerSummaryCard>
				<Title $size='lg'>{summary?.user_details?.organisation_name || row?.user_details?.organisation_name || '--'}</Title>
				<SummaryRow>
					<Pill color='info' variant='outline'>
						{strings('po_amount')}: {displayAmount(summary?.total_po_amount || 0)}
					</Pill>
					<Pill color='error' variant='outline'>
						{strings('total_required_limit')}: {displayAmount(summary?.total_required_credit_limit || 0)}
					</Pill>
				</SummaryRow>
				<SummaryRow>
					<KeyValue>
						<KeyLabel>{strings('unutilized_credit_limit')}</KeyLabel>
						<KeyVal $color='info'>{displayAmount(summary?.available_credit_limit || 0)}</KeyVal>
					</KeyValue>
				</SummaryRow>
			</DrawerSummaryCard>
			<Table columns={columns} data={enquiries} rowClickable={false} emptyState={{ text: strings('no_data_found') }} hideFooter />
		</DrawerBodyWrapper>
	)
}

export default UnapprovedEnquiries
